import { useTranslation } from 'react-i18next'
import { useCopilot } from '../hooks/useCopilot'
import { Phase } from '../state/types'

const PHASE_GLYPH: Record<Phase, string> = {
  empty: '○',
  uploading: '↑',
  creating: '✎',
  queued: '⏳',
  streaming: '⋯',
  awaiting_confirmation: '⏸',
  awaiting_answer: '?',
  complete: '✓',
  error: '✕',
  interrupted: '⊘',
}

// Tone class drives the glyph colour (go / stop / amber) on the ink tab strip.
const PHASE_TONE: Record<Phase, string> = {
  empty: 'idle',
  uploading: 'working',
  creating: 'working',
  queued: 'working',
  streaming: 'working',
  awaiting_confirmation: 'wait',
  awaiting_answer: 'wait',
  complete: 'done',
  error: 'error',
  interrupted: 'idle',
}

interface SessionSwitcherProps {
  onNew: () => void
  // Called when an existing tab is picked, so the panel can leave the new-draft view.
  onSelect?: () => void
}

export function SessionSwitcher({ onNew, onSelect }: Readonly<SessionSwitcherProps>) {
  const { t } = useTranslation()
  const { state, setActive } = useCopilot()
  if (state.order.length === 0) return null

  return (
    <div className="copilot-switcher" role="tablist">
      {state.order.map((cid) => {
        const s = state.sessions[cid]
        const active = cid === state.activeId
        return (
          <button
            key={cid}
            type="button"
            role="tab"
            aria-selected={active}
            className={`copilot-switcher__tab is-${PHASE_TONE[s.phase]}${active ? ' is-active' : ''}`}
            onClick={() => { setActive(cid); onSelect?.() }}
          >
            <span className="copilot-switcher__glyph" aria-hidden="true">{PHASE_GLYPH[s.phase]}</span>
            <span className="copilot-switcher__title">{s.title}</span>
            {s.unread && (
              <span
                className="copilot-switcher__dot"
                aria-label={t('copilot.unread')}
                data-testid={`unread-dot-${cid}`}
              />
            )}
          </button>
        )
      })}
      <button
        type="button"
        className="copilot-switcher__new"
        data-testid="copilot-new-session"
        onClick={onNew}
      >
        {t('copilot.newSession')}
      </button>
    </div>
  )
}
